import { format } from '@rental/i18n'
import { Button, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type ErrorComponentProps, useRouter } from '@tanstack/react-router'
import { PageHeader } from '../components/PageHeader'
import { useLocale } from '../i18n'
import { referenceFor } from '../telemetry'

/**
 * S2: what a screen that stopped working shows, inside the app's frame so
 * the menu stays. Reported by the router's onCatch (router.tsx) before this
 * renders; the reference lets the Owner quote the report. Try again re-runs
 * the route; Reload starts the app afresh.
 *
 * The answer that broke the screen may still be cached, and fresh for 30
 * seconds: re-rendering from it would only break the screen again. So a retry
 * first drops every cached answer no screen is showing (the broken screen's
 * among them, since it is gone), and the screen asks the API afresh. Who is
 * signed in lives outside this cache (access-context.tsx).
 */
export function CrashScreen({ error, reset }: ErrorComponentProps) {
  const { messages: m } = useLocale()
  const router = useRouter()
  const queryClient = useQueryClient()
  const reference = referenceFor(error)
  return (
    <>
      <PageHeader title={m.crash.title} />
      <Notice tone="danger" live="assertive">
        <p>{m.crash.body}</p>
        {reference ? (
          <p className="text-sm text-text-muted">{format(m.crash.reference, { id: reference })}</p>
        ) : null}
      </Notice>
      <div className="flex flex-wrap gap-3">
        <Button
          variant="staff"
          onClick={() => {
            queryClient.removeQueries({ type: 'inactive' })
            reset()
            void router.invalidate()
          }}
        >
          {m.crash.retry}
        </Button>
        <Button variant="outline" className="rounded-sm" onClick={() => window.location.reload()}>
          {m.crash.reload}
        </Button>
      </div>
    </>
  )
}
